import { useEffect, useMemo, useState } from 'react';
import { useDataContext } from '../context/DataContext';
import { ENDPOINTS } from '../services/apiService';
import { transformData } from './utils';
import { useLoadingStore } from '../store/loadingStore';

const processEmissionDistributionData = (data: void | any[]) => {
  if (!data) return;
  const updated = data
    .map((item: any) => {
      if (item.type === 'Gathering and Boosting Unit') {
        item.type = 'Gathering and Boosting Station';
      }

      if (
        [
          'Wellpad',
          'Compressor Station',
          'Gathering and Boosting Station',
          'Gas Processing Plant',
          'Dehydration Facility',
        ].includes(item.type)
      ) {
        return item;
      }
      return undefined;
    })
    .filter((item) => item)
    .sort((a, b) => {
      const order = [
        'Wellpad',
        'Compressor Station',
        'Gathering and Boosting Station',
        'Gas Processing Plant',
        'Dehydration Facility',
      ];

      const orderA = order.indexOf(a.type);
      const orderB = order.indexOf(b.type);

      if (orderA > orderB) {
        return 1;
      } else {
        return -1;
      }
    });

  return updated;
};

export const useFacilityLevel = ({
  period,
  time,
}: {
  period: string;
  time: string;
}) => {
  const yearsArray = useMemo(() => {
    if (period.includes('All')) {
      return ['2024'];
    } else {
      const periodParts = period.split(' ');
      return [periodParts[1]];
    }
  }, [period]);

  const quartersArray = useMemo(() => {
    if (period.includes('All')) {
      return ['1', '2', '3', '4'];
    } else {
      const periodParts = period.split(' ');
      return [periodParts[0].slice(1)];
    }
  }, [period]);

  const [allResults, setAllResults] = useState<any[]>([]);
  const [leakerResults, setLeakerResults] = useState<any[]>([]);
  const [totalResults, setTotalResults] = useState<any[]>([]);

  const { fetchMultipleAndSetData } = useDataContext();
  const setLoading = useLoadingStore(
    (state: { setLoading: any }) => state.setLoading,
  );

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      try {
        const results = await fetchMultipleAndSetData([
          {
            type: ENDPOINTS.facility_data,
            params: {
              quarter: quartersArray.join(','),
              year: yearsArray.join(','),
              sponsored: '1',
              og: '1',
              facility_type: time,
            },
          },
          {
            type: ENDPOINTS.facility_summary,
            params: {
              quarter: quartersArray.join(','),
              year: yearsArray.join(','),
              facility_type: time,
            },
          },
        ]);

        if (results) {
          const facilityData = results.find(
            (r) => r.type === ENDPOINTS.facility_data + '#' + 0,
          )?.data;
          const facilitySummaryData = results.find(
            (r) => r.type === ENDPOINTS.facility_summary + '#' + 1,
          )?.data;

          const transformedData = transformData(facilityData);
          const updatedData = processEmissionDistributionData(transformedData);
          setAllResults(updatedData || []);

          setLeakerResults(facilitySummaryData || []);
          setTotalResults(facilitySummaryData || []);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setLoading(false);
      } finally {
        setTimeout(() => setLoading(false), 7000);
      }
    };

    fetchData();
  }, [
    fetchMultipleAndSetData,
    period,
    time,
    quartersArray,
    yearsArray,
    setLoading,
  ]);

  return { allResults, totalResults, leakerResults };
};
